// React and core libraries
import * as React from "react";
import {
  useParams,
  Link,
  useLocation,
  useNavigate,
  useMatch,
  resolvePath,
} from "react-router-dom";

// Third-party libraries
import { useQuery } from "react-query";

// Internal components
import { Page } from "../../components/layout/page";
import { Card, CardHeader } from "../../components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

// Internal utilities, hooks, and contexts
import { ApiClient, parseError } from "../../apiUtils";
import { Urls } from "../../domain/urls";
import { UserContext } from "../../crud/user/context";
import { useAccountDetails } from "../../crud/account/hooks";
import { WorkspaceContext } from "../../utils/context";
import { useProgram } from "../../crud/pricing/hooks";
import { useTimezone } from "../../utils/hooks";
import { useScopedContext } from "../../shared-components/ScopeSelector";
import { formatInTimeZone } from "date-fns-tz";

// Relative imports (components from same directory)
import { ClusterContext } from "./context";
import { CloudBackendChip } from "./Information/CloudBackendChip";
import { ClusterState } from "./Information/ClusterStateComponent";
import { MetricFlags } from "./MetricFlags";
import { StopButton, PauseButton } from "./StopButton";
import { DashboardButton, ExternalLinkButton } from "./DashboardButton";

import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ContainerAPISchema } from "@/api-client";

const DATE_TIME_FORMAT = "yyyy-MM-dd HH:mm:ss 'UTC'xxx";

interface ClusterNavigationTabsProps {
  clusterId: string;
  accountSlug: string;
  cluster: any; // TODO: type this properly
}

const ClusterNavigationTabs = ({
  clusterId,
  accountSlug,
  cluster,
}: ClusterNavigationTabsProps): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = [
    {
      label: "Information",
      value: "information",
      href: `/${Urls.Clusters}/${clusterId}/account/${accountSlug}/information`,
    },
    {
      label: "Infrastructure",
      value: "infrastructure",
      href: `/${Urls.Clusters}/${clusterId}/account/${accountSlug}/infrastructure`,
    },
  ];

  const currentPath = useMatch(location.pathname)?.pathname;

  // Check if current path matches any tab value exactly
  let currentTabValue = "information"; // default
  if (currentPath) {
    const matchingTab = tabs.find((tab) => {
      const resolvedHref = resolvePath(tab.href).pathname;
      return (
        currentPath === resolvedHref || currentPath.startsWith(resolvedHref)
      );
    });
    if (matchingTab) {
      currentTabValue = matchingTab.value;
    }
  }

  const isNotebook = cluster?.clusterType === "notebook" || false;
  const buttonProps = {
    accountSlug,
    clusterId: cluster?.id ?? 0,
    clusterCreatorId: cluster?.creator.id ?? 0,
    clusterCreatorName: cluster?.creator.username ?? "",
    clusterState: cluster?.currentState.state ?? "",
    isNotebook,
  };

  const handleTabChange = (value: string) => {
    const tab = tabs.find((t) => t.value === value);
    if (tab) {
      navigate(tab.href);
    }
  };

  return (
    <div className="flex items-center justify-between w-full">
      <Tabs value={currentTabValue} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-2 bg-transparent">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.href}
              value={tab.value}
              className="text-lg pb-3 py-4 border-b-3 border-transparent"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {cluster && (
        <div className="flex items-center gap-2 ml-4">
          {cluster?.scheduler?.spec.containers
            // .filter((c: ContainerAPISchema) => !!c.exposedUserPorts)
            .map((c: ContainerAPISchema) =>
              c.exposedUserPorts?.map((port: number) => (
                <ExternalLinkButton
                  key={`sidecar_${c.name}_${port}`}
                  url={`http://${cluster.scheduler?.instance?.publicIpAddress}:${port}`}
                  label={c.name}
                  enabled={
                    !!(
                      cluster?.scheduler?.currentState?.state === "started" &&
                      cluster.scheduler?.instance?.publicIpAddress
                    )
                  }
                  disabledReason="Not currently running"
                />
              )),
            )}
          <DashboardButton
            dashboardAddress={cluster?.scheduler?.dashboardAddress}
            isNotebook={isNotebook}
            schedulerState={cluster?.scheduler?.currentState?.state}
          />
          {cluster?.pauseOnExit ? (
            <PauseButton {...buttonProps} pauseOnExit />
          ) : (
            <StopButton {...buttonProps} pauseOnExit={false} />
          )}
        </div>
      )}
    </div>
  );
};

interface ClusterLayoutProps {
  children: React.ReactNode;
}

export const ClusterLayout = ({
  children,
}: ClusterLayoutProps): React.ReactElement => {
  const clusterId = useParams().clusterId as string;
  const accountSlug = useParams().accountSlug as string;
  const [shouldFetch, setShouldFetch] = React.useState(true);
  const { user } = React.useContext(UserContext);
  const [, setScope] = useScopedContext();
  const [displayTz] = useTimezone();
  const { data: workspace, isSuccess: workspaceLoaded } = useAccountDetails({
    workspaceSlug: accountSlug,
  });

  const { data: activeProgram } = useProgram(workspace?.organization?.id);

  const { isLoading, data, error } = useQuery(
    ["fetchCluster", clusterId],
    () =>
      ApiClient.declarativeViewsClusterClusterGetByIdForFrontend({
        pk: Number(clusterId),
      }),
    {
      refetchInterval: 5000,
      enabled: shouldFetch,
    },
  );

  const detailsBreadCrumbs = React.useMemo(
    () => [
      {
        text: "Clusters",
        link: `/${Urls.Clusters}?workspace=${
          data?.account.name || accountSlug
        }`,
        onClick: () => {
          setScope({
            type: "account",
            name: data?.account.name as string,
            id: data?.accountId as number,
            slug: data?.account.name as string,
          });
        },
      },
      {
        text: (
          <>
            <div className="max-w-[calc(100vw-700px)] min-w-[50px] overflow-hidden text-ellipsis inline-flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger>
                  <span className="cursor-help">{data?.name}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <div>{data?.name}</div>
                  {data?.created && (
                    <div>
                      <b>Created:</b>{" "}
                      {formatInTimeZone(
                        new Date(data?.created),
                        displayTz,
                        DATE_TIME_FORMAT,
                      )}
                      <br />
                      <b>Finished:</b>{" "}
                      {data?.finishedAt &&
                        formatInTimeZone(
                          new Date(data?.finishedAt),
                          displayTz,
                          DATE_TIME_FORMAT,
                        )}
                    </div>
                  )}
                  {user.isStaff && (
                    <div>
                      {data?.account.slug}/
                      <Link
                        to={`mailto:${data?.creator.fullName}<${data?.creator.email}>?subject=Coiled&bcc=emails-to-coiled-users@coiled.io`}
                      >
                        {data?.creator.username}
                      </Link>
                      <div>
                        <Link
                          to={`https://app.hubspot.com/search/9245528/search?query=${encodeURI(data?.creator?.email || "")}`}
                        >
                          Hubspot
                        </Link>
                      </div>
                      <div>Tier: {activeProgram?.tier}</div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
              {data && <ClusterState state={data.currentState} />}
              {data && user.isStaff && (
                <CloudBackendChip
                  hosted={data?.coiledHosted}
                  backend={data.backendType}
                />
              )}
              <MetricFlags metricFlags={data?.metricFlags} />
            </div>
          </>
        ),
      },
    ],
    [data, accountSlug, displayTz, user.isStaff, activeProgram?.tier, setScope],
  );

  if (
    (data ? ["error", "stopped"].includes(data.currentState.state) : false) &&
    shouldFetch
  ) {
    setShouldFetch(false);
  }
  if (!workspaceLoaded) {
    return <div />;
  }

  return (
    <ClusterContext.Provider
      value={{
        error: error ? parseError(error) : undefined,
        loading: isLoading,
        clusterId,
        cluster: data,
      }}
    >
      <Page title="Cluster Details" breadCrumbs={detailsBreadCrumbs}>
        <Card>
          <CardHeader className="border-b-1 border-gray-200">
            <ClusterNavigationTabs
              clusterId={clusterId}
              accountSlug={accountSlug}
              cluster={data}
            />
          </CardHeader>
          <WorkspaceContext.Provider value={workspace}>
            {children}
          </WorkspaceContext.Provider>
        </Card>
      </Page>
    </ClusterContext.Provider>
  );
};
